import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Icon from '../../../../components/Icon';
import { FiEdit2, FiUser, FiPhone, FiMail, FiBriefcase } from 'react-icons/fi';
import type { Manager } from '../../service/managersService';

type ManagerDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    manager: Manager | null;
    bankId?: string;
    onUpdate?: () => void;
};

const TYPE_OPTIONS = [
    { value: 'BranchManager', label: 'Branch Manager' },
    { value: 'OperationalManager', label: 'Operational Manager' },
    { value: 'AreaOperationManager', label: 'Area Operation Manager' },
];

export default function ManagerDetailsModal({ isOpen, onClose, manager, bankId, onUpdate }: ManagerDetailsModalProps) {
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const [isEditing, setIsEditing] = useState(false);
    const [editedManager, setEditedManager] = useState<Manager | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (manager) {
            setEditedManager({ ...manager });
        }
    }, [manager]);

    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
        }
    }, [isOpen]);

    const handleFieldChange = (field: string, value: string) => {
        if (editedManager) {
            setEditedManager({ ...editedManager, [field]: value });
        }
    };

    const handleSave = async () => {
        if (!editedManager || !bankId || !editedManager.id) return;

        setLoading(true);
        try {
            const managerRef = doc(db, 'banks', bankId, 'managers', editedManager.id);
            const updateData = { ...editedManager };
            delete (updateData as any).id;

            await updateDoc(managerRef, updateData);

            if (onUpdate) {
                onUpdate();
            }

            setIsEditing(false);
            showToast('✅ Manager updated successfully!', 'success');
        } catch (err: any) {
            console.error('Error updating manager:', err);
            showToast('❌ Failed to update manager', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (manager) {
            setEditedManager({ ...manager });
        }
        setIsEditing(false);
    };

    const handleClose = () => {
        setIsEditing(false);
        onClose();
    };

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        // Toast will be handled by parent component
        console.log(message, type);
    };

    if (!manager || !editedManager) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Manager Details"
            size="lg"
        >
            <div className="p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Edit Button in Header Area - Only show for bank users */}
                {isBankUser && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            aria-label="Edit manager details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <TextField
                        label="Name"
                        value={editedManager.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        placeholder="Enter manager name"
                        readOnly={!isBankUser || !isEditing}
                        prefix={<Icon icon={FiUser} className="text-gray-400" />}
                    />

                    <TextField
                        label="Mobile"
                        type="tel"
                        value={editedManager.mobile || ''}
                        onChange={(e) => handleFieldChange('mobile', e.target.value)}
                        placeholder="Enter mobile number"
                        readOnly={!isBankUser || !isEditing}
                        prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                    />

                    <TextField
                        label="Email"
                        type="email"
                        value={editedManager.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        placeholder="Enter email address"
                        readOnly={!isBankUser || !isEditing}
                        prefix={<Icon icon={FiMail} className="text-gray-400" />}
                    />

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Type
                        </label>
                        <div className="relative">
                            <Icon
                                icon={FiBriefcase}
                                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                            />
                            <select
                                value={editedManager.type || ''}
                                onChange={(e) => handleFieldChange('type', e.target.value)}
                                disabled={!isBankUser || !isEditing}
                                className={`w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${!isBankUser || !isEditing ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''
                                    }`}
                            >
                                <option value="">Select Type</option>
                                {TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}

